import { Progress } from '@agro-club/agroclub-shared'
import { useFormik } from 'formik'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { SharedModalContent, SharedModalTitle } from 'views/components/SharedModal'
import { SharedModalInputs } from 'views/components/SharedModal/styled'

import { CarrierFormControls } from '../../components/CarrierFormControls/CarrierFormControls'
import { CarrierRole, CarrierType } from '../../types'
import { Carrier } from 'modules/domain/logisticsKR/types'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { AnalyticsPlaces } from '../../analyticsPlaces'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { useAFormHandler } from 'analytics/hooks'
import { useNotification } from 'hooks/useNotification'
import { FormFields } from './types'
import { RuCompanyControl } from './CompanyFormControls/RuCompanyControl'

type Props = {
  onClose: () => void
  onSuccess: (val: Carrier) => void
  initialValues?: {
    carrier_role: Carrier['carrier_role']
  }
  setValue?: (val) => void
  title?: string
}

export const AddCarrierModal: FC<Props> = ({ onClose, onSuccess, title, initialValues }) => {
  const { t } = useTranslation('logisticsKR')
  const { formProgress, formHandler } = useAFormHandler('addCarrier')
  const notify = useNotification()

  const formik = useFormik<FormFields>({
    initialValues: {
      inn: '', // inn will be overritten by dadataInfo
      name: '',
      phone: '',
      contact_name: '',
      is_in_charter: false,
      has_vat: false,
      working_regions: [],
      favorite_regions: [],
      carrier_type: CarrierType.local,
      carrier_role: initialValues?.carrier_role ?? [CarrierRole.owner],
      dadata: undefined,
    },

    onSubmit: formHandler(
      async () => {
        const { dadata, ...dto } = formik.values
        return await apiClient.post<Carrier>(endpoints.LogisticsKR.carriers(), {
          ...dto,
          dadata,
          name: dadata?.name_full ?? dadata?.name_short ?? null,
          inn: dadata.inn ?? null,
        })
      },
      {
        onSuccess: async (response) => {
          await onSuccess(response)
          onClose()
        },
        onError: (error) => {
          const { errors } = error
          formik.setErrors(errors)
          notify(Progress.ERROR, { title: t('carriers.notifications.carrier_add_error') })
        },
      },
    ),
  })

  return (
    <ASharedModal id={AnalyticsPlaces.CARRIERS.ADD_MODAL} size="medium" onClose={onClose}>
      <SharedModalTitle>{title || t('carriers.form.addTitle')}</SharedModalTitle>
      <SharedModalContent>
        <SharedModalInputs>
          <RuCompanyControl formik={formik} />
          <CarrierFormControls formik={formik} />
        </SharedModalInputs>
      </SharedModalContent>
      <ADefaultModalFooter
        onClose={onClose}
        progress={formProgress}
        onConfirm={() => formik.submitForm()}
        confirmButtonText={t('common:save')}
      />
    </ASharedModal>
  )
}
